import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Settings, ClipboardList, ClipboardPlus, History, Package, Users as UsersIcon, LogOut, Home, TrendingUp } from 'lucide-react';
import QuickMaintenanceDialog from './QuickMaintenanceDialog';
import QuickWorkOrderDialog from './QuickWorkOrderDialog';
import { MaintenanceRequestsMenu } from './MaintenanceRequestsMenu';

/**
 * Shared header action group used on every top-level page.
 * Renders a gear-icon dropdown with: Add Maintenance Log (admin), Dashboard,
 * Maintenance History, Inventory, Users (admin), Logout.
 * Also exposes an optional "onQuickAdd" callback if the parent wants to hook
 * into the maintenance log dialog directly.
 */
const HeaderMenu = ({ user, onLogout, showHomeLink = true }) => {
  const navigate = useNavigate();
  const [showQuickMaintenanceDialog, setShowQuickMaintenanceDialog] = useState(false);
  const [showQuickWorkOrderDialog, setShowQuickWorkOrderDialog] = useState(false);
  const isAdmin = user?.role === 'admin' || user?.role === 'technician';

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" data-testid="header-menu-btn" aria-label="Open menu">
            <Settings className="h-5 w-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56" data-testid="header-menu-content">
          <DropdownMenuLabel className="text-xs text-slate-500">
            Signed in as {user?.name}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {showHomeLink && (
            <DropdownMenuItem
              onSelect={() => navigate('/')}
              data-testid="menu-dashboard-btn"
            >
              <Home className="mr-2 h-4 w-4" />
              Dashboard
            </DropdownMenuItem>
          )}
          {isAdmin && (
            <DropdownMenuItem
              onSelect={() => setShowQuickMaintenanceDialog(true)}
              data-testid="menu-quick-maintenance-btn"
            >
              <ClipboardList className="mr-2 h-4 w-4" />
              Add Maintenance Log
            </DropdownMenuItem>
          )}
          {isAdmin && (
            <DropdownMenuItem
              onSelect={() => setShowQuickWorkOrderDialog(true)}
              data-testid="menu-quick-work-order-btn"
            >
              <ClipboardPlus className="mr-2 h-4 w-4" />
              Create Work Order
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onSelect={() => navigate('/maintenance-history')}
            data-testid="menu-maintenance-history-btn"
          >
            <History className="mr-2 h-4 w-4" />
            Maintenance History
          </DropdownMenuItem>
          <MaintenanceRequestsMenu />
          <DropdownMenuItem
            onSelect={() => navigate('/inventory')}
            data-testid="menu-inventory-btn"
          >
            <Package className="mr-2 h-4 w-4" />
            Inventory
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => navigate('/analytics')}
            data-testid="menu-analytics-btn"
          >
            <TrendingUp className="mr-2 h-4 w-4" />
            Analytics
          </DropdownMenuItem>
          {user?.role === 'admin' && (
            <DropdownMenuItem
              onSelect={() => navigate('/admin/users')}
              data-testid="menu-users-btn"
            >
              <UsersIcon className="mr-2 h-4 w-4" />
              Users
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={onLogout} data-testid="menu-logout-btn">
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <QuickMaintenanceDialog
        open={showQuickMaintenanceDialog}
        onClose={() => setShowQuickMaintenanceDialog(false)}
        onSuccess={() => setShowQuickMaintenanceDialog(false)}
      />

      <QuickWorkOrderDialog
        open={showQuickWorkOrderDialog}
        onClose={() => setShowQuickWorkOrderDialog(false)}
        onSuccess={() => setShowQuickWorkOrderDialog(false)}
      />
    </>
  );
};

export default HeaderMenu;
